import { useMemo, useState } from 'react';
import { VOLUME_GROUPS } from '../logic/muscles';
import { FONT_MONO } from '../theme';
import { Card } from '../ui';
import { CardTitle, PillGroup, extraColors } from './common';
import { fmtSets, muscleRows, shortDate, volumeVerdict } from './statsData';

const VIEWS = [
  { id: 'week', label: 'This week' },
  { id: 'avg', label: '4-wk avg' },
];

function zoneColors(t, c) {
  return {
    below: { bar: t.textMuted, band: 'transparent', label: 'below MEV' },
    productive: { bar: t.accent, band: t.accentSoft, label: 'productive' },
    high: { bar: c.amber, band: c.amberSoft, label: 'high' },
    over: { bar: t.danger, band: t.dangerSoft, label: 'above MRV' },
  };
}

/* Hard sets per muscle (primary 1, secondary ½) as bullet bars: the track is
 * shaded by the muscle's rough landmarks, the bar is the sets done and the
 * tick is what the plan gives per week. Each muscle has its own scale, since
 * the point is where it sits in ITS range. */
export function MuscleVolumeCard({ t, isDark, volume, plan, exerciseMeta, onAssign }) {
  const [view, setView] = useState('week');
  const [openMuscle, setOpenMuscle] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const c = extraColors(isDark);
  const zones = zoneColors(t, c);

  const values = view === 'avg' ? volume.avg : volume.thisWeek;
  const rows = useMemo(
    () => muscleRows(values, volume.planned, volume.landmarks),
    [values, volume.planned, volume.landmarks]
  );
  // A week in progress can't be judged, so the verdict reads the full weeks
  // before it, or the plan until there is one.
  const fromAvg = volume.avgWeeks > 0;
  const verdict = useMemo(
    () => volumeVerdict(fromAvg ? volume.avg : volume.planned, volume.landmarks, { plan, exerciseMeta }),
    [fromAvg, volume.avg, volume.planned, volume.landmarks, plan, exerciseMeta]
  );
  const verdictWarn = verdict.zone !== null;

  const sub = view === 'avg'
    ? (fromAvg
      ? `Average of the last ${volume.avgWeeks === 1 ? 'full week' : `${volume.avgWeeks} full weeks`}. Hard sets; secondary muscles count ½.`
      : 'No full week logged yet.')
    : `Since Mon ${shortDate(volume.weekStart)}. Hard sets; secondary muscles count ½.`;

  return (
    <Card t={t} padding={16}>
      <CardTitle t={t} sub={sub} right={<PillGroup t={t} label="Week or average" options={VIEWS} value={view} onChange={setView} />}>
        Weekly sets per muscle
      </CardTitle>

      <div style={{
        fontSize: 12, lineHeight: 1.45, color: t.text, borderRadius: 8, padding: '8px 10px', marginBottom: 12,
        background: verdictWarn ? c.amberSoft : t.accentSoft,
        border: `1px solid ${verdictWarn ? c.amberSoft : t.accentBorder}`,
      }}>
        <span style={{ color: t.textMuted }}>{fromAvg ? (volume.avgWeeks === 1 ? 'Last week: ' : `Last ${volume.avgWeeks} weeks: `) : 'Your plan: '}</span>
        {verdict.text}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {rows.map(r => {
          const lm = r.landmark;
          const scale = Math.max(lm ? lm.mrv : 0, r.sets, r.plan, 1) * 1.1;
          const pct = v => `${Math.min(100, (v / scale) * 100)}%`;
          const z = zones[r.zone] || zones.below;
          const open = openMuscle === r.muscle;
          return (
            <div key={r.muscle}>
              <button type="button" onClick={() => setOpenMuscle(open ? null : r.muscle)} aria-expanded={open} style={{
                display: 'grid', gridTemplateColumns: '84px minmax(0, 1fr) 80px', gap: 8, alignItems: 'center',
                width: '100%', minHeight: 40, padding: '0 4px', background: 'transparent', border: 'none',
                cursor: 'pointer', textAlign: 'left',
              }}>
                <span style={{ fontSize: 12, color: t.text, whiteSpace: 'nowrap' }}>{r.muscle}</span>
                <span style={{ position: 'relative', height: 14, borderRadius: 4, overflow: 'hidden', background: t.divider, display: 'block' }}>
                  {lm && (
                    <>
                      <span style={{ position: 'absolute', top: 0, bottom: 0, left: pct(lm.mev), width: `calc(${pct(lm.mavHigh)} - ${pct(lm.mev)})`, background: zones.productive.band }} />
                      <span style={{ position: 'absolute', top: 0, bottom: 0, left: pct(lm.mavHigh), width: `calc(${pct(lm.mrv)} - ${pct(lm.mavHigh)})`, background: zones.high.band }} />
                      <span style={{ position: 'absolute', top: 0, bottom: 0, left: pct(lm.mrv), right: 0, background: zones.over.band }} />
                    </>
                  )}
                  <span style={{ position: 'absolute', top: 4, height: 6, left: 0, width: pct(r.sets), borderRadius: 3, background: z.bar }} />
                  {r.plan > 0 && (
                    <span title={`Plan ${fmtSets(r.plan)}/wk`} style={{
                      position: 'absolute', top: 0, bottom: 0, width: 2, marginLeft: -1, left: pct(r.plan), background: t.text, opacity: 0.75,
                    }} />
                  )}
                </span>
                <span style={{ fontSize: 12, fontFamily: FONT_MONO, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {/* Nothing done is never "productive", even where MEV is 0. */}
                  <b style={{ color: r.zone === 'below' || r.sets === 0 ? t.textMuted : z.bar }}>{fmtSets(r.sets)}</b>
                  <span style={{ color: t.textFaint, fontSize: 11 }}> / {fmtSets(r.plan)}</span>
                </span>
              </button>
              {open && lm && (
                <div style={{ fontSize: 11, color: t.textMuted, padding: '0 4px 8px 96px', lineHeight: 1.45, overflowWrap: 'anywhere' }}>
                  Rough range: {lm.mev > 0 ? `under ${lm.mev} too little · ` : ''}{lm.mev}-{lm.mavHigh} productive
                  {' '}· {lm.mavHigh}-{lm.mrv} high · over {lm.mrv} more than most recover from.
                  {' '}Plan {fmtSets(r.plan)}/wk.
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', marginTop: 10, fontSize: 11, color: t.textFaint }}>
        {['below', 'productive', 'high', 'over'].map(k => (
          <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 12, height: 6, borderRadius: 3, background: zones[k].bar, display: 'inline-block' }} />
            {zones[k].label}
          </span>
        ))}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 2, height: 12, background: t.text, opacity: 0.75, display: 'inline-block' }} />
          plan / wk
        </span>
      </div>
      <div style={{ fontSize: 11, color: t.textFaint, marginTop: 6, lineHeight: 1.5 }}>
        Shaded ranges are rough guides for most lifters, not targets. On a cut, the low end of productive is fine.
        Tap a muscle for its numbers.
      </div>

      {volume.unmapped.length > 0 && (
        <div style={{ marginTop: 14, borderTop: `1px solid ${t.divider}`, paddingTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: t.text }}>Not counted</div>
          <div style={{ fontSize: 11, color: t.textFaint, marginTop: 2, marginBottom: 6 }}>
            No muscle known for these. Assign one so their sets count.
          </div>
          {volume.unmapped.map(name => (
            <div key={name} style={{ borderBottom: `1px solid ${t.divider}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44 }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: t.text, overflowWrap: 'anywhere' }}>{name}</span>
                {assigning !== name && (
                  <button type="button" onClick={() => setAssigning(name)} style={{
                    minHeight: 40, padding: '0 14px', borderRadius: 999, cursor: 'pointer',
                    border: `1px solid ${t.accentBorder}`, background: t.accentSoft, color: t.accent,
                    fontSize: 12, fontWeight: 700,
                  }}>Assign</button>
                )}
              </div>
              {assigning === name && (
                <AssignEditor t={t} onCancel={() => setAssigning(null)}
                  onSave={(primary, secondary) => { onAssign(name, primary, secondary); setAssigning(null); }} />
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function ChoiceChip({ t, active, onClick, children }) {
  return (
    <button type="button" aria-pressed={active} onClick={onClick} style={{
      minHeight: 40, padding: '0 12px', borderRadius: 999, cursor: 'pointer',
      border: `1px solid ${active ? t.accent : t.cardBorder}`,
      background: active ? t.accentSoft : 'transparent',
      color: active ? t.accent : t.textMuted, fontSize: 12, fontWeight: 600,
    }}>{children}</button>
  );
}

/* Writes exerciseMeta[name] = { primary, secondary }; Cardio means "never
 * count it as volume" (a bike or a sled push). */
function AssignEditor({ t, onSave, onCancel }) {
  const [primary, setPrimary] = useState('');
  const [secondary, setSecondary] = useState([]);
  const toggle = m => setSecondary(s => (s.includes(m) ? s.filter(x => x !== m) : [...s, m]));
  const label = { fontSize: 11, fontWeight: 700, color: t.textFaint, letterSpacing: '0.04em', textTransform: 'uppercase', margin: '6px 0' };
  return (
    <div style={{ padding: '4px 0 12px' }}>
      <div style={label}>Main muscle</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {[...VOLUME_GROUPS, 'Cardio'].map(m => (
          <ChoiceChip key={m} t={t} active={primary === m}
            onClick={() => { setPrimary(m); setSecondary(s => s.filter(x => x !== m)); }}>{m}</ChoiceChip>
        ))}
      </div>
      {primary && primary !== 'Cardio' && (
        <>
          <div style={label}>Also works (counts ½)</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {VOLUME_GROUPS.filter(m => m !== primary).map(m => (
              <ChoiceChip key={m} t={t} active={secondary.includes(m)} onClick={() => toggle(m)}>{m}</ChoiceChip>
            ))}
          </div>
        </>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button type="button" disabled={!primary}
          onClick={() => onSave(primary, primary === 'Cardio' ? [] : secondary)} style={{
            flex: 1, minHeight: 44, borderRadius: 10, border: `1px solid ${t.accent}`,
            background: t.accent, color: '#fff', fontSize: 13, fontWeight: 700,
            cursor: primary ? 'pointer' : 'not-allowed', opacity: primary ? 1 : 0.5,
          }}>Save</button>
        <button type="button" onClick={onCancel} style={{
          flex: 1, minHeight: 44, borderRadius: 10, border: `1px solid ${t.cardBorder}`,
          background: t.surfaceAlt, color: t.text, fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>Cancel</button>
      </div>
    </div>
  );
}
